import React, { useState, useEffect, useRef } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/utils/utils';

interface MultiSelectProps {
    options: { label: string, value: number }[],
    value: number[],
    onChange: (value: number[]) => void,
    placeholder?: string,
    fieldState?: any,
    className?: string
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
    options,
    fieldState,
    value,
    onChange,
    placeholder = '',
    className
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleSelect = (selectedValue: number) => {
        if (value.includes(selectedValue)) {
            onChange(value.filter(v => v !== selectedValue)); // Remove if already selected
        } else {
            onChange([...value, selectedValue]); // Add if not selected
        }
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setIsOpen(false); // Đóng dropdown nếu click ra ngoài
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className={cn('relative', className)} ref={dropdownRef}>
            {/* Trigger */}
            <button
                className={`relative flex h-12 w-full items-center text-input-1 rounded-lg border border-input bg-background pl-3 pr-10 py-4 focus:outline-none ${fieldState.error ? 'border-red-500' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                type='button'
            >
                <span className='truncate'>
                    {value.length > 0 ?
                        options
                            .filter(option => value.includes(option.value))
                            .map(option => option.label)
                            .join(', ') :
                        placeholder}
                </span>
                <ChevronDown className='absolute right-3 h-4 w-4 opacity-50 text-nav-1' />
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className='absolute z-50 mt-2 max-h-96 w-full overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md'>
                    {options.map(option => (
                        <div
                            key={option.value}
                            className='flex items-center cursor-pointer select-none rounded-sm py-1.5 pl-8 pr-2 text-sm hover:bg-accent hover:text-accent-foreground'
                            onClick={() => handleSelect(option.value)}
                        >
                            <span className='absolute left-2 flex h-3.5 w-3.5 items-center justify-center'>
                                {value.includes(option.value) && <Check className='h-4 w-4' />}
                            </span>
                            <span>{option.label}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
